import {
  Shape,
  DraftShape,
  FactoryShape,
  Storey,
  MezzanineStorey,
  NonMezzanineStorey,
} from '@shared/models/design/storey.model';
import { guards } from '../typeguards.helpers';
import {
  Building,
  BuildingDesign,
} from '@shared/models/design/building-design.model';
import {
  Activity,
  ActivityMap,
} from '@shared/models/design/activities/activity.model';
import {
  ActivityType,
  activityTypes,
} from '@shared/models/design/activities/activity-type.model';

/**
 * Check if the shape is a draft shape, a shape that is being edited and can't be saved
 * @param shape Shape to check
 * @returns
 */
const isDraftShape = (
  shape?: Shape | DraftShape | FactoryShape,
): shape is DraftShape =>
  guards.is.object(shape) && 'draft' in shape && shape.draft === true;

const isStorey = (storey?: unknown): storey is Storey =>
  guards.is.object(storey) &&
  'shapes' in storey &&
  guards.is.array(storey.shapes);

const isBuildingDesign = (design?: unknown): design is BuildingDesign =>
  guards.is.object(design) &&
  'buildings' in design &&
  guards.is.array(design.buildings);

const isBuilding = (building?: unknown): building is Building =>
  guards.is.object(building) &&
  'storeys' in building &&
  guards.is.array(building.storeys);

const isShape = (shape?: unknown): shape is Shape =>
  guards.is.object(shape) &&
  'points' in shape &&
  guards.is.array(shape.points) &&
  guards.is.string(shape.id);

const isMezzanineStorey = (storey?: unknown): storey is MezzanineStorey =>
  isStorey(storey) && 'mezzanine' in storey && storey.mezzanine === true;

const isNonMezzanineStorey = (storey?: unknown): storey is NonMezzanineStorey =>
  isStorey(storey) && !isMezzanineStorey(storey);

/**
 * Check if the activity is a valid activity
 * @param activity Activity to check
 * @returns
 */
const isActivity = (activity?: unknown): activity is Activity =>
  guards.is.object(activity) &&
  'type' in activity &&
  isActivityType(activity.type);

const isActivityType = (activityType?: unknown): activityType is ActivityType =>
  guards.is.string(activityType) &&
  activityTypes.includes(activityType as ActivityType);

const isActivityOfType = <T extends ActivityType>(
  activity: unknown,
  type: T,
): activity is ActivityMap[T] => isActivity(activity) && activity.type === type;

const assertActivityOfType = <T extends ActivityType>(
  activity: unknown,
  type: T,
): asserts activity is ActivityMap[T] => {
  if (!isActivityOfType(activity, type)) {
    throw new Error(`Activity is not of type ${type}`);
  }
};

/**
 * Guards for design related types (WIP)
 */
export const designGuards = {
  is: {
    draftShape: isDraftShape,
    shape: isShape,
    storey: isStorey,
    nonMezzanineStorey: isNonMezzanineStorey,
    mezzanineStorey: isMezzanineStorey,
    buildingDesign: isBuildingDesign,
    building: isBuilding,
    activity: isActivity,
    activityType: isActivityType,
    activityOfType: isActivityOfType,
  },
  assert: {
    activityOfType: assertActivityOfType,
  },
} as const;
